import { DateTime } from 'luxon';

import type { CalDavCalendar } from '@/features/caldav/types';

import type {
  CalendarEvent,
  CalendarEventWithColor,
  DayColumn,
  PositionedEvent,
  WeekViewData,
} from '../types';
import { CALENDAR_VIEW_CONSTANTS } from '../types';

const { HOUR_HEIGHT, MIN_EVENT_HEIGHT, DAYS_IN_WEEK, HOURS_IN_DAY } = CALENDAR_VIEW_CONSTANTS;

/**
 * Minimum visual duration in minutes — matches MIN_EVENT_HEIGHT on the pixel grid.
 * Used so that visually-expanded short events are treated as occupying their rendered
 * footprint during collision detection, preventing overlap with adjacent events.
 */
const MIN_VISUAL_MINUTES = (MIN_EVENT_HEIGHT / HOUR_HEIGHT) * 60;

/**
 * Returns the effective end time of an event for layout purposes.
 * If the event is shorter than MIN_EVENT_HEIGHT, the end is extended so that
 * collision detection accounts for the expanded visual block.
 */
function getEffectiveEnd(event: CalendarEventWithColor): DateTime {
  const start = DateTime.fromISO(event.startsAt);
  const end = DateTime.fromISO(event.endsAt);
  const minEnd = start.plus({ minutes: MIN_VISUAL_MINUTES });
  return end < minEnd ? minEnd : end;
}

/**
 * Default color for events when calendar color is not available.
 */
const DEFAULT_EVENT_COLOR = '#808080';

/**
 * Check if an event has a color property (from API).
 */
function hasColor(event: CalendarEvent): event is CalendarEventWithColor {
  return 'color' in event && typeof (event as CalendarEventWithColor).color === 'string';
}

/**
 * Build complete week view data from events and calendars.
 */
export function buildWeekViewData(
  events: CalendarEvent[] | CalendarEventWithColor[],
  calendars: CalDavCalendar[],
  weekStart: DateTime,
): WeekViewData {
  const colorMap = new Map(calendars.map((c) => [c.id, c.color ?? DEFAULT_EVENT_COLOR]));
  const weekEnd = weekStart.plus({ days: 6 }).endOf('day');

  // Add color to events (use existing color from API if present)
  const eventsWithColor: CalendarEventWithColor[] = events.map((e) => ({
    ...e,
    color: hasColor(e) ? e.color : (colorMap.get(e.calendarId) ?? DEFAULT_EVENT_COLOR),
  }));

  const days: DayColumn[] = [];

  for (let i = 0; i < DAYS_IN_WEEK; i++) {
    const date = weekStart.plus({ days: i });
    const dayStart = date.startOf('day');
    const dayEnd = date.endOf('day');

    // Filter events that overlap with this day
    const dayEvents = eventsWithColor.filter((e) => {
      const eventStart = parseEventDateTime(e.startsAt, e.isAllDay);
      const eventEnd = parseEventDateTime(e.endsAt, e.isAllDay);
      return eventStart <= dayEnd && eventEnd >= dayStart;
    });

    const allDayEvents = dayEvents.filter((e) => e.isAllDay);
    const timedEvents = dayEvents.filter((e) => !e.isAllDay);

    // Handle events crossing midnight - split them for display
    const processedTimedEvents = processTimedEventsForDay(timedEvents, dayStart, dayEnd);

    days.push({
      date,
      allDayEvents,
      timedEvents: layoutEvents(processedTimedEvents, dayStart),
    });
  }

  return { weekStart, weekEnd, days };
}

/**
 * Parse event datetime, handling both ISO datetime and date-only strings.
 */
function parseEventDateTime(dateStr: string, isAllDay: boolean): DateTime {
  if (isAllDay) {
    // All-day events use date-only format
    return DateTime.fromISO(dateStr).startOf('day');
  }
  return DateTime.fromISO(dateStr);
}

/**
 * Process timed events for a specific day, handling events that cross midnight.
 * Returns events clipped to the day boundaries.
 */
function processTimedEventsForDay(
  events: CalendarEventWithColor[],
  dayStart: DateTime,
  dayEnd: DateTime,
): CalendarEventWithColor[] {
  return events.map((event) => {
    const eventStart = DateTime.fromISO(event.startsAt);
    const eventEnd = DateTime.fromISO(event.endsAt);

    // Clip event to day boundaries
    const clippedStart = eventStart < dayStart ? dayStart : eventStart;
    const clippedEnd = eventEnd > dayEnd ? dayEnd : eventEnd;

    return {
      ...event,
      startsAt: clippedStart.toISO()!,
      endsAt: clippedEnd.toISO()!,
    };
  });
}

/**
 * Layout events with collision detection and positioning.
 * Uses the "collision groups" algorithm to arrange overlapping events side by side.
 */
function layoutEvents(events: CalendarEventWithColor[], dayStart: DateTime): PositionedEvent[] {
  if (events.length === 0) return [];

  // Sort by start time, then by duration (longer first for better visual layout)
  const sorted = [...events].sort((a, b) => {
    const startDiff =
      DateTime.fromISO(a.startsAt).toMillis() - DateTime.fromISO(b.startsAt).toMillis();
    if (startDiff !== 0) return startDiff;

    // If same start time, put longer events first
    const aDuration =
      DateTime.fromISO(a.endsAt).toMillis() - DateTime.fromISO(a.startsAt).toMillis();
    const bDuration =
      DateTime.fromISO(b.endsAt).toMillis() - DateTime.fromISO(b.startsAt).toMillis();
    return bDuration - aDuration;
  });

  // Find collision groups
  const groups = findCollisionGroups(sorted);

  // Position events within each group
  const result: PositionedEvent[] = [];

  for (const group of groups) {
    const columns = assignColumns(group);
    const totalColumns = Math.max(...columns.map((c) => c.column)) + 1;
    const spanned = computeSpans(columns, totalColumns);

    for (const { event, column, span } of spanned) {
      const eventStart = DateTime.fromISO(event.startsAt);
      const eventEnd = DateTime.fromISO(event.endsAt);

      const startMinutes = eventStart.diff(dayStart, 'minutes').minutes;
      const eventDurationMinutes = eventEnd.diff(eventStart, 'minutes').minutes;

      // Expand block if matched worklog extends beyond the event
      let displayDurationMinutes = eventDurationMinutes;
      if (event.matchedWorklog && event.matchedWorklog.duration > 0) {
        let wlOffsetMin = 0;
        if (event.matchedWorklog.start) {
          const wlStart = DateTime.fromISO(event.matchedWorklog.start);
          if (wlStart.isValid) {
            wlOffsetMin = Math.max(0, wlStart.diff(eventStart, 'minutes').minutes);
          }
        }
        const wlEndFromEventStart = wlOffsetMin + event.matchedWorklog.duration;
        displayDurationMinutes = Math.max(displayDurationMinutes, wlEndFromEventStart);
      }

      // Cap at end of day so oversized worklogs don't overflow the grid
      const maxDurationMinutes = HOURS_IN_DAY * 60 - startMinutes;
      displayDurationMinutes = Math.min(displayDurationMinutes, maxDurationMinutes);

      const top = (startMinutes / 60) * HOUR_HEIGHT;
      const height = Math.max((displayDurationMinutes / 60) * HOUR_HEIGHT, MIN_EVENT_HEIGHT);
      const colWidth = 100 / totalColumns;
      const width = colWidth * span;
      const left = column * colWidth;

      result.push({
        event,
        top,
        height,
        left,
        width,
        columnIndex: column,
        totalColumns,
      });
    }
  }

  return result;
}

/**
 * For each event, compute how many consecutive columns (starting from its own)
 * are free during its time range, so it can expand to fill unused space.
 */
function computeSpans(
  columns: Array<{ event: CalendarEventWithColor; column: number }>,
  totalColumns: number,
): Array<{ event: CalendarEventWithColor; column: number; span: number }> {
  // Build column → effective time ranges map (using visual footprint for short events)
  const columnRanges = new Map<number, Array<{ start: DateTime; end: DateTime }>>();
  for (const { event, column } of columns) {
    if (!columnRanges.has(column)) {
      columnRanges.set(column, []);
    }
    columnRanges.get(column)!.push({
      start: DateTime.fromISO(event.startsAt),
      end: getEffectiveEnd(event),
    });
  }

  return columns.map(({ event, column }) => {
    const eventStart = DateTime.fromISO(event.startsAt);
    const effectiveEnd = getEffectiveEnd(event);

    let span = 1;
    for (let nextCol = column + 1; nextCol < totalColumns; nextCol++) {
      const ranges = columnRanges.get(nextCol) ?? [];
      const hasOverlap = ranges.some((r) => r.start < effectiveEnd && r.end > eventStart);
      if (hasOverlap) break;
      span++;
    }

    return { event, column, span };
  });
}

/**
 * Find groups of overlapping events.
 * Events in the same group have overlapping time ranges.
 */
function findCollisionGroups(sortedEvents: CalendarEventWithColor[]): CalendarEventWithColor[][] {
  const groups: CalendarEventWithColor[][] = [];
  let currentGroup: CalendarEventWithColor[] = [];
  let groupEnd = DateTime.fromMillis(0);

  for (const event of sortedEvents) {
    const eventStart = DateTime.fromISO(event.startsAt);
    const effectiveEnd = getEffectiveEnd(event);

    if (currentGroup.length === 0 || eventStart < groupEnd) {
      // Event overlaps with current group (visually or by time)
      currentGroup.push(event);
      if (effectiveEnd > groupEnd) {
        groupEnd = effectiveEnd;
      }
    } else {
      // Event starts after current group ends - start new group
      groups.push(currentGroup);
      currentGroup = [event];
      groupEnd = effectiveEnd;
    }
  }

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

/**
 * Assign column positions to events within a collision group.
 * Uses a greedy algorithm to pack events into the minimum number of columns.
 */
function assignColumns(
  group: CalendarEventWithColor[],
): Array<{ event: CalendarEventWithColor; column: number }> {
  const result: Array<{ event: CalendarEventWithColor; column: number }> = [];
  const columnEnds: DateTime[] = []; // Tracks when each column becomes free

  for (const event of group) {
    const eventStart = DateTime.fromISO(event.startsAt);
    const effectiveEnd = getEffectiveEnd(event);

    // Find the first column where this event can fit (comparing visual footprints)
    let column = -1;
    for (let i = 0; i < columnEnds.length; i++) {
      if (columnEnds[i] <= eventStart) {
        column = i;
        break;
      }
    }

    if (column === -1) {
      // Need a new column
      column = columnEnds.length;
      columnEnds.push(effectiveEnd);
    } else {
      columnEnds[column] = effectiveEnd;
    }

    result.push({ event, column });
  }

  return result;
}
